import {AlertTriangle, Check, Plus, UserMinus, X} from 'lucide-react';
import {computeAge, daysUntilAge, formatDate} from '@common/lib/utils';
import {Button} from '@common-web/components/ui/button';
import {DataTable, type Column} from '@common-web/components/data-table';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {MemberStatusBadge} from '@client/pages/client/member-profile/MemberStatusBadge';

export type MemberDependentRow = {
  memberSeq: string;
  firstName: string | null;
  lastName: string | null;
  relationshipLabel: string;
  birthDate: string | null;
  isActive: boolean;
  /** Enrollment date, shown in the internal Effective column. */
  effectiveDate?: string | null;
  /** Whether the dependent has any active plan, for the internal Coverage column. */
  hasCoverage?: boolean;
};

type MemberDependentsSectionProps = {
  dependents: MemberDependentRow[];
  canManage: boolean;
  onAddDependent?: () => void;
  onDropDependent?: (row: MemberDependentRow) => void;
};

const AGE_OUT_AGE = 26;
const AGE_OUT_WARN_DAYS = 60;

/** Demo-parity dependents card for the internal portal: prominent header with
 *  an active count and a solid Add Dependent CTA, DOB/Age with age-out warning
 *  chips, Effective and Coverage columns, soft status badges, icon actions. */
function InternalDependentsSection({
  dependents,
  canManage,
  onAddDependent,
  onDropDependent,
}: MemberDependentsSectionProps) {
  const activeCount = dependents.filter((d) => d.isActive).length;

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3'>
        <div>
          <h3 className='text-[16px] font-semibold text-foreground'>
            Dependents ({activeCount} active)
          </h3>
          <p className='text-[13px] text-muted-foreground'>
            All dependents on this member, including terminated records.
          </p>
        </div>
        {canManage && onAddDependent && (
          <Button type='button' size='sm' onClick={onAddDependent}>
            <Plus className='mr-1.5 h-3.5 w-3.5' />
            Add Dependent
          </Button>
        )}
      </div>

      {dependents.length === 0 ? (
        <div className='rounded-lg border px-6 py-10 text-center text-[13px] text-muted-foreground'>
          No dependents on file.
        </div>
      ) : (
        <div className='overflow-hidden rounded-lg border'>
          <table className='w-full text-[13px]'>
            <thead className='bg-muted/50'>
              <tr className='text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                <th className='px-4 py-3'>Name</th>
                <th className='px-4 py-3'>Relationship</th>
                <th className='px-4 py-3'>DOB / Age</th>
                <th className='px-4 py-3'>Status</th>
                <th className='px-4 py-3'>Effective</th>
                <th className='px-4 py-3'>Coverage</th>
                {canManage && <th className='w-20 px-4 py-3' />}
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {dependents.map((d) => {
                const name = [d.firstName, d.lastName].filter(Boolean).join(' ') || '—';
                const age = d.birthDate ? computeAge(d.birthDate) : null;
                const isChild = /child|other/i.test(d.relationshipLabel);
                const daysToAge =
                  isChild && d.isActive && d.birthDate
                    ? daysUntilAge(d.birthDate, AGE_OUT_AGE)
                    : null;
                const ageOutClose =
                  daysToAge !== null && daysToAge > 0 && daysToAge <= AGE_OUT_WARN_DAYS;
                const ageOutVeryClose = daysToAge !== null && daysToAge > 0 && daysToAge <= 30;
                return (
                  <tr key={d.memberSeq}>
                    <td className='px-4 py-3 font-medium text-foreground'>{name}</td>
                    <td className='px-4 py-3 text-muted-foreground'>
                      {d.relationshipLabel || '—'}
                    </td>
                    <td className='px-4 py-3 text-muted-foreground'>
                      <div>{formatDate(d.birthDate, 'MMM D, YYYY', '—')}</div>
                      {age !== null && (
                        <div className='text-xs text-muted-foreground/80'>
                          Age {age}
                          {ageOutClose && (
                            <span
                              className={`ml-2 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                                ageOutVeryClose
                                  ? 'bg-[#FEE2E2] text-[#991B1B]'
                                  : 'bg-[#FEF3C7] text-[#92400E]'
                              }`}
                            >
                              <AlertTriangle className='h-2.5 w-2.5' /> Ages out in {daysToAge}d
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className='px-4 py-3'>
                      <MemberStatusBadge
                        status={d.isActive ? 'active' : 'termed'}
                        size='context'
                        className='px-2.5'
                      />
                    </td>
                    <td className='px-4 py-3 text-muted-foreground'>
                      {formatDate(d.effectiveDate ?? null, 'MMM D, YYYY', '—')}
                    </td>
                    <td className='px-4 py-3'>
                      {d.hasCoverage ? (
                        <Check className='h-3.5 w-3.5 text-green-600' />
                      ) : (
                        <X className='h-3.5 w-3.5 text-muted-foreground' />
                      )}
                    </td>
                    {canManage && (
                      <td className='px-4 py-3'>
                        {d.isActive && onDropDependent && (
                          <button
                            type='button'
                            onClick={() => onDropDependent(d)}
                            className='rounded p-1.5 text-red-500 hover:bg-red-50'
                            title='Remove'
                            aria-label={`Remove ${name}`}
                          >
                            <UserMinus className='h-3.5 w-3.5' />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function MemberDependentsSection({
  dependents,
  canManage,
  onAddDependent,
  onDropDependent,
}: MemberDependentsSectionProps) {
  const isInternal = useIsInternal();
  const activeCount = dependents.filter((d) => d.isActive).length;

  if (isInternal) {
    return (
      <InternalDependentsSection
        dependents={dependents}
        canManage={canManage}
        onAddDependent={onAddDependent}
        onDropDependent={onDropDependent}
      />
    );
  }

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <h3 className='text-xs font-semibold uppercase tracking-wide text-brand-gray'>
            Dependents
          </h3>
          <span className='rounded-full bg-brand-blue/10 px-1.5 py-0.5 text-xs font-semibold text-brand-blue'>
            {activeCount} active
          </span>
        </div>
        {canManage && onAddDependent && (
          <Button type='button' size='sm' variant='outline' onClick={onAddDependent}>
            Add Dependent
          </Button>
        )}
      </div>
      <DataTable<MemberDependentRow>
        columns={[
          {
            id: 'name',
            header: 'Name',
            render: (row) => [row.lastName, row.firstName].filter(Boolean).join(', ') || '—',
          },
          {
            id: 'relationship',
            header: 'Relationship',
            render: (row) => row.relationshipLabel || '—',
          },
          {
            id: 'dob',
            header: 'Date of Birth',
            render: (row) => formatDate(row.birthDate, 'MMM D, YYYY', '—'),
          },
          {
            id: 'status',
            header: 'Status',
            render: (row) => <MemberStatusBadge status={row.isActive ? 'active' : 'termed'} />,
          },
          ...(canManage && onDropDependent
            ? [
                {
                  id: 'actions',
                  header: '',
                  render: (row) =>
                    row.isActive ? (
                      <Button
                        type='button'
                        size='sm'
                        variant='ghost'
                        className='text-red-600 hover:text-red-700'
                        onClick={() => onDropDependent(row)}
                      >
                        Remove
                      </Button>
                    ) : null,
                } satisfies Column<MemberDependentRow>,
              ]
            : []),
        ]}
        data={dependents}
        rowKey={(row) => row.memberSeq}
        emptyMessage='No dependents on file.'
      />
    </div>
  );
}
